"use client";

import React, { useEffect, useRef } from "react";
import { BiDollar } from "react-icons/bi";

import Input from "@/components/ui/input";

interface DollarInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  value: number;
  onValueChange: (value: number) => void;
  min?: number;
  max?: number;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  hasError?: boolean;
  errorMessage?: string;
  allowNegative?: boolean;
}

const DollarInput: React.FC<DollarInputProps> = ({
  value,
  onValueChange,
  min,
  max,
  placeholder,
  className,
  disabled,
  hasError,
  errorMessage,
  allowNegative = false,
  ...props
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const inputElement = inputRef.current;

    if (inputElement) {
      const handleWheel: EventListener = (event) => {
        event.preventDefault();
      };

      inputElement.addEventListener("wheel", handleWheel, {
        passive: false,
      } as EventListenerOptions);

      return () => {
        if (inputElement) {
          inputElement.removeEventListener("wheel", handleWheel, {
            passive: false,
          } as EventListenerOptions);
        }
      };
    }
  }, []);

  return (
    <Input
      className={className}
      ref={inputRef}
      type="number"
      id="amount"
      name="amount"
      autoComplete="amount"
      step={0.01}
      disabled={disabled}
      enableStepper={false}
      hasError={hasError}
      errorMessage={errorMessage}
      placeholder={value === 0 ? placeholder : undefined}
      value={value === 0 ? "" : value}
      icon={BiDollar}
      {...props}
      onChange={(e) => {
        const rawValue = e.target.value;
        const isNegative = allowNegative && rawValue.startsWith("-");

        // Remove all non-numeric characters except decimal point (and minus if allowed)
        let cleanValue = allowNegative
          ? rawValue.replace(/[^\d.-]/g, "")
          : rawValue.replace(/[^\d.]/g, "");

        // Ensure only one minus sign at the start
        if (allowNegative && isNegative) {
          cleanValue = "-" + cleanValue.replace(/-/g, "");
        }

        // Handle the case where the input starts with a decimal point
        if (cleanValue.startsWith(".")) {
          cleanValue = "0" + cleanValue;
        } else if (cleanValue.startsWith("-.")) {
          cleanValue = "-0" + cleanValue.substring(1);
        }

        const parts = cleanValue.split(".");
        let adjustedValue: number;

        if (parts.length > 1) {
          // If there's a decimal part, limit it to 2 digits
          adjustedValue = parseFloat(parts[0] + "." + parts[1].slice(0, 2));
        } else {
          // If there's no decimal part, parse as is
          adjustedValue = parseFloat(cleanValue);
        }

        if (isNaN(adjustedValue)) {
          adjustedValue = 0;
        }

        // Round to 2 decimal places
        adjustedValue = Math.round(adjustedValue * 100) / 100;

        if (min !== undefined && adjustedValue < min) {
          adjustedValue = min;
        } else if (max !== undefined && adjustedValue > max) {
          adjustedValue = max;
        }

        onValueChange(adjustedValue);
      }}
      min={min}
      max={max}
    />
  );
};

export default DollarInput;
